import EmptyState from '@c/dashboard/EmptyState';
import { ReviewSummary } from '@c/dashboard/pages/RatingsPage';
import { NoteIcon } from '@c/icons';
import SafeImage from '@ui/SafeImage';
import { StarIcon } from 'lucide-react';
import { ReviewDocument } from 'models/review';
import { PublicUserDocument } from 'models/user';

export default function SellerProfileReviews({
  reviews,
  user,
  onChangeTab,
}: {
  reviews: ReviewDocument[];
  user: PublicUserDocument;
  onChangeTab: () => void;
}) {
  const displayedReviews = reviews
    .sort((a, b) => b.rating - a.rating || b.created - a.created)
    .slice(0, 3);
  const positiveReviews = reviews.filter((review) => review.rating >= 3);
  const negativeReviews = reviews.filter((review) => review.rating < 3);

  return (
    <>
      {reviews.length > 0 ? (
        <div className="mt-8 flex w-full flex-col-reverse gap-[12rem] px-4 py-5 sm:flex-row">
          <div className="flex w-full flex-col gap-[3rem] pr-8">
            <div className="flex grow items-center justify-between px-4">
              <div className="flex items-center gap-x-4">
                <StarIcon className="h-[3rem] w-[3rem] fill-brand-secondary text-brand-secondary" />

                <span className="pt-2 text-[2.5rem] font-bold -tracking-[0.1rem] text-black">
                  {user.rating.toFixed(1)}
                </span>

                <span className="pt-1 text-[1.8rem] font-medium text-brand-gray">
                  ({user.reviews} reviews)
                </span>
              </div>

              <button
                type="button"
                onClick={onChangeTab}
                className="text-[1.7rem] font-semibold text-brand-secondary"
              >
                See All
              </button>
            </div>

            <div className="flex w-full gap-[2rem] overflow-scroll">
              {displayedReviews.map((review) => (
                <ReviewCard review={review} key={review.id} />
              ))}
            </div>
          </div>
        </div>
      ) : user.reviews > 0 ? (
        <div className="mt-[5rem]">
          <ReviewSummary
            userDoc={user}
            positiveReviews={positiveReviews.length}
            negativeReviews={negativeReviews.length}
          />
        </div>
      ) : (
        <div className="mt-[5rem]">
          <EmptyState
            icon={<NoteIcon />}
            title={'No Reviews Yet!'}
            description={
              'This seller has no reviews yet. Be the first to review them!'
            }
          />
        </div>
      )}
    </>
  );
}

const ReviewCard = ({ review }: { review: ReviewDocument }) => {
  return (
    <div className="flex h-[20rem] w-full max-w-[40rem] flex-col justify-between gap-y-8 rounded-[2rem] border border-zinc-200 bg-[#F6F6F6] p-8">
      <div className="flex flex-col gap-y-4">
        <div className="flex items-center gap-x-4">
          {[...Array(5)].map((_, i) => (
            <StarIcon
              key={i}
              className={`h-[2rem] w-[2rem] ${
                i < review.rating
                  ? 'fill-brand-secondary text-brand-secondary'
                  : 'stroke-zinc-600 text-zinc-300'
              }`}
            />
          ))}
        </div>

        {review.comment ? (
          <p className="line-clamp-2 text-[1.8rem] font-medium text-brand-gray">
            {review.comment}
          </p>
        ) : (
          <p className="text-[1.8rem] font-medium text-brand-gray/60">
            This review has no comment.
          </p>
        )}
      </div>

      <div className="flex items-center gap-x-4">
        <SafeImage
          src={review.reviewer_photo}
          alt={review.reviewer_username}
          className="h-[5rem] w-[5rem] rounded-full object-cover"
          height={200}
          width={200}
        />

        <div className="flex flex-col gap-y-1">
          <span className="text-[1.5rem] font-semibold text-black">
            {review.reviewer_username}
          </span>

          <span className="text-[1.5rem] font-medium text-brand-gray">
            {new Date(review.created).getTime() >
            Date.now() - 1000 * 60 * 60 * 24 * 30
              ? `${Math.ceil(
                  (Date.now() - new Date(review.created).getTime()) /
                    (1000 * 60 * 60 * 24)
                )}d ago`
              : `${Math.ceil(
                  (Date.now() - new Date(review.created).getTime()) /
                    (1000 * 60 * 60 * 24 * 30)
                )}mo ago`}
          </span>
        </div>
      </div>
    </div>
  );
};
